import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';

const RbacPage = ({ section = 'permission-groups' }) => {
  const { user: loggedInUser, businesses, permissions: loginPermissions } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({});

  // Edit modal state (permission-groups)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ permission_group_name: '', permission_group_description: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [disablingId, setDisablingId] = useState(null);

  // Edit modal state (permissions)
  const [showPermEditModal, setShowPermEditModal] = useState(false);
  const [permEditItem, setPermEditItem] = useState(null);
  const [permEditForm, setPermEditForm] = useState({ permission_group_id: '', permission_name: '', permission_description: '', action: 'read', route: '' });
  const [permEditSubmitting, setPermEditSubmitting] = useState(false);
  const [permDisablingId, setPermDisablingId] = useState(null);

  // Edit modal state (roles)
  const [showRoleEditModal, setShowRoleEditModal] = useState(false);
  const [roleEditItem, setRoleEditItem] = useState(null);
  const [roleEditForm, setRoleEditForm] = useState({ role_name: '', role_description: '' });
  const [roleEditSubmitting, setRoleEditSubmitting] = useState(false);
  const [roleDisablingId, setRoleDisablingId] = useState(null);

  // Permission groups for dropdown
  const [permissionGroups, setPermissionGroups] = useState([]);

  // Role-Permission mapping state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const sectionConfig = {
    'permission-groups': {
      title: 'Permission Groups',
      description: 'Manage permission group categories',
      icon: 'FolderKey',
      fields: [
        { name: 'permission_group_name', label: 'Group Name', type: 'text', required: true },
        { name: 'permission_group_description', label: 'Description', type: 'text' },
      ],
      columns: ['Group ID', 'Group Name', 'Description', 'Status', 'Actions'],
      dataKeys: ['group_id', 'group_name', 'group_description', 'status'],
      fetch: () => apiService?.getPermissionGroups(),
      create: (data) => apiService?.createPermissionGroup(data),
    },
    'permissions': {
      title: 'Permissions',
      description: 'Define system permissions',
      icon: 'Lock',
      fields: [
        { name: 'permission_group_id', label: 'Permission Group', type: 'permission_group_select', required: true },
        { name: 'permission_name', label: 'Permission Name', type: 'text', required: true },
        { name: 'permission_description', label: 'Description', type: 'text' },
        { name: 'action', label: 'Action', type: 'select', options: ['read', 'write', 'delete', 'manage'], required: true },
        { name: 'route', label: 'Route', type: 'text', required: true },
      ],
      columns: ['Permission ID', 'Permission Name', 'Route', 'Action', 'Description', 'Status', 'Actions'],
      dataKeys: ['permission_id', 'permission_name', 'route', 'action', 'permission_description', 'status'],
      fetch: () => apiService?.getPermissions(),
      create: (data) => apiService?.createPermission(data),
    },
    'roles': {
      title: 'Roles',
      description: 'Manage user roles',
      icon: 'UserCog',
      fields: [
        { name: 'role_name', label: 'Role Name', type: 'text', required: true },
        { name: 'role_description', label: 'Description', type: 'text' },
        { name: 'business_code', label: 'Business', type: 'business_select', required: true },
      ],
      columns: ['Role ID', 'Role Name', 'Business', 'Description', 'Status', 'Actions'],
      dataKeys: ['role_id', 'role_name', 'business_code', 'role_description', 'status'],
      fetch: () => apiService?.getRoles(businesses),
      create: (data) => apiService?.createRole(data),
    },
  };

  const config = sectionConfig?.[section];

  useEffect(() => {
    fetchData();
    if (section === 'role-permissions' || section === 'user-roles') {
      fetchSupportingData();
    }
    if (section === 'permissions') {
      fetchPermissionGroups();
    }
  }, [section]);

  const fetchPermissionGroups = async () => {
    try {
      const res = await apiService?.getPermissionGroups();
      const data = res?.data;
      const list = data?.details || data?.data || data?.items || data || [];
      setPermissionGroups(Array.isArray(list) ? list : []);
    } catch (err) {}
  };

  const fetchData = async () => {
    setLoading(true); setError('');
    try {
      let response;
      if (section === 'role-permissions') {
        response = await apiService?.getAllRolePermissions(businesses);
      } else if (section === 'user-roles') {
        response = await apiService?.getAllUserRoles(businesses, loggedInUser?.user_type?.id);
      } else if (config) {
        response = await config?.fetch();
      }
      const data = response?.data;
      const list = data?.details || data?.data || data?.items || data || [];
      if (section === 'role-permissions' || section === 'user-roles') {
        setMappings(Array.isArray(list) ? list : []);
      } else {
        setItems(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportingData = async () => {
    try {
      const userType = loggedInUser?.user_type?.id;
      const [rolesRes, permsRes, usersRes] = await Promise.all([
        apiService?.getRoles(businesses),
        apiService?.getPermissions(),
        apiService?.getUsers(businesses, userType),
      ]);
      const rolesData = rolesRes?.data;
      const permsData = permsRes?.data;
      const usersData = usersRes?.data;
      setRoles(Array.isArray(rolesData?.details) ? rolesData?.details : Array.isArray(rolesData?.data) ? rolesData?.data : Array.isArray(rolesData?.items) ? rolesData?.items : Array.isArray(rolesData) ? rolesData : []);
      setPermissions(Array.isArray(permsData?.details) ? permsData?.details : Array.isArray(permsData?.data) ? permsData?.data : Array.isArray(permsData?.items) ? permsData?.items : Array.isArray(permsData) ? permsData : []);
      setUsers(Array.isArray(usersData?.details) ? usersData?.details : Array.isArray(usersData?.data) ? usersData?.data : Array.isArray(usersData?.items) ? usersData?.items : Array.isArray(usersData) ? usersData : []);
    } catch (err) {}
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setError('');
    try {
      await config?.create(form);
      setSuccess(`${config?.title?.slice(0, -1)} created successfully!`);
      setShowForm(false);
      setForm({});
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  // Permission Group edit/disable handlers
  const handleOpenEdit = (item) => {
    setEditItem(item);
    setEditForm({
      permission_group_name: item?.group_name || '',
      permission_group_description: item?.group_description || '',
    });
    setShowEditModal(true);
    setError('');
  };

  const handleUpdate = async (e) => {
    e?.preventDefault();
    if (!editItem?.group_id) return;
    setEditSubmitting(true); setError('');
    try {
      await apiService?.updatePermissionGroup(editItem?.group_id, {
        permission_group_name: editForm?.permission_group_name,
        permission_group_description: editForm?.permission_group_description,
      });
      setSuccess('Permission group updated successfully!');
      setShowEditModal(false);
      setEditItem(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update permission group');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDisable = async (item) => {
    const isActive = item?.status === 1;
    setDisablingId(item?.group_id);
    setError('');
    try {
      await apiService?.disablePermissionGroup(item?.group_id, {
        is_active: isActive ? 'false' : 'true',
      });
      setSuccess(`Permission group ${isActive ? 'disabled' : 'enabled'} successfully!`);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${isActive ? 'disable' : 'enable'} permission group`);
    } finally {
      setDisablingId(null);
    }
  };

  // Permission edit/disable handlers
  const handleOpenPermEdit = (item) => {
    setPermEditItem(item);
    setPermEditForm({
      permission_group_id: item?.permission_group_id || '',
      permission_name: item?.permission_name || '',
      permission_description: item?.permission_description || '',
      action: item?.action || 'read',
      route: item?.route || '',
    });
    setShowPermEditModal(true);
    setError('');
  };

  const handlePermUpdate = async (e) => {
    e?.preventDefault();
    if (!permEditItem?.permission_id) return;
    setPermEditSubmitting(true); setError('');
    try {
      await apiService?.updatePermission(permEditItem?.permission_id, {
        permission_group_id: permEditForm?.permission_group_id,
        permission_name: permEditForm?.permission_name,
        permission_description: permEditForm?.permission_description,
        action: permEditForm?.action,
        route: permEditForm?.route,
      });
      setSuccess('Permission updated successfully!');
      setShowPermEditModal(false);
      setPermEditItem(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update permission');
    } finally {
      setPermEditSubmitting(false);
    }
  };

  const handlePermDisable = async (item) => {
    const isActive = item?.status === 1;
    setPermDisablingId(item?.permission_id);
    setError('');
    try {
      await apiService?.disablePermission(item?.permission_id, {
        is_active: isActive ? 'false' : 'true',
      });
      setSuccess(`Permission ${isActive ? 'disabled' : 'enabled'} successfully!`);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${isActive ? 'disable' : 'enable'} permission`);
    } finally {
      setPermDisablingId(null);
    }
  };

  // Role edit/disable handlers
  const handleOpenRoleEdit = (item) => {
    setRoleEditItem(item);
    setRoleEditForm({
      role_name: item?.role_name || '',
      role_description: item?.role_description || '',
    });
    setShowRoleEditModal(true);
    setError('');
  };

  const handleRoleUpdate = async (e) => {
    e?.preventDefault();
    if (!roleEditItem?.role_id) return;
    setRoleEditSubmitting(true); setError('');
    try {
      await apiService?.updateRole(roleEditItem?.role_id, {
        role_name: roleEditForm?.role_name,
        role_description: roleEditForm?.role_description,
      });
      setSuccess('Role updated successfully!');
      setShowRoleEditModal(false);
      setRoleEditItem(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update role');
    } finally {
      setRoleEditSubmitting(false);
    }
  };

  const handleRoleDisable = async (item) => {
    const isActive = item?.status === 1;
    setRoleDisablingId(item?.role_id);
    setError('');
    try {
      await apiService?.disableRole(item?.role_id, {
        is_active: isActive ? 'false' : 'true',
      });
      setSuccess(`Role ${isActive ? 'disabled' : 'enabled'} successfully!`);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${isActive ? 'disable' : 'enable'} role`);
    } finally {
      setRoleDisablingId(null);
    }
  };

  const handleAssignPermissions = async () => {
    if (!selectedRole || selectedPermissions?.length === 0) return;
    setSubmitting(true); setError('');
    try {
      await apiService?.assignPermissionsToRole(selectedRole, { permissions: selectedPermissions });
      setSuccess('Permissions assigned to role successfully!');
      setSelectedPermissions([]);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to assign permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignRoles = async () => {
    if (!selectedUser || selectedRoles?.length === 0) return;
    setSubmitting(true); setError('');
    try {
      await apiService?.assignRolesToUser(selectedUser, { role_ids: selectedRoles });
      setSuccess('Roles assigned to user successfully!');
      setSelectedRoles([]);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to assign roles');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePermission = (id) => {
    setSelectedPermissions(prev => prev?.includes(id) ? prev?.filter(p => p !== id) : [...prev, id]);
  };

  const toggleRole = (id) => {
    setSelectedRoles(prev => prev?.includes(id) ? prev?.filter(r => r !== id) : [...prev, id]);
  };

  const inputClass = "w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  const renderRolePermissions = () => (
    <div className="space-y-5">
      {/* Assign Permissions to Role */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Assign Permissions to Role</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">Select Role</label>
            <select value={selectedRole} onChange={e => {
              const roleId = e?.target?.value;
              setSelectedRole(roleId);
              if (roleId) {
                const existingMapping = mappings?.find(m => m?.role?.role_id === roleId);
                const existingPermIds = existingMapping?.permissions?.map(p => p?.permission_id)?.filter(Boolean) || [];
                setSelectedPermissions(existingPermIds);
              } else {
                setSelectedPermissions([]);
              }
            }} className={inputClass}>
              <option value="">Choose a role...</option>
              {roles?.map(r => <option key={r?.role_id} value={r?.role_id}>{r?.role_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-2">Select Permissions</label>
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
              {permissions?.map(p => {
                const isAllowed = loginPermissions?.some(lp => lp?.permission_id === p?.permission_id);
                return (
                  <label key={p?.permission_id} className={`flex items-center space-x-2 p-2 rounded ${isAllowed ? 'hover:bg-muted cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                    <input type="checkbox" checked={selectedPermissions?.includes(p?.permission_id)}
                      onChange={() => isAllowed && togglePermission(p?.permission_id)}
                      disabled={!isAllowed}
                      className="h-4 w-4 rounded border-input text-primary disabled:cursor-not-allowed" />
                    <span className={`text-sm ${isAllowed ? 'text-text-primary' : 'text-text-secondary'}`}>{p?.permission_name}</span>
                    <span className="text-xs text-text-secondary">({p?.action})</span>
                  </label>
                );
              })}
            </div>
          </div>
          <button onClick={handleAssignPermissions} disabled={!selectedRole || selectedPermissions?.length === 0 || submitting}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
            {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Assigning...</span></> : <span>Assign Permissions</span>}
          </button>
        </div>
      </div>

      {/* Existing Mappings */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-text-primary">Role-Permission Mappings</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {['Role', 'Assigned Permissions', 'Assignment ID']?.map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="text-center py-8"><Icon name="Loader2" size={20} className="animate-spin text-primary mx-auto" /></td></tr>
              ) : mappings?.length === 0 ? (
                <tr><td colSpan={3} className="text-center py-8 text-text-secondary text-sm">No mappings found</td></tr>
              ) : mappings?.map((m, i) => (
                <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30 align-top">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-text-primary">{m?.role?.role_name || '-'}</div>
                    <div className="text-xs font-mono text-text-secondary mt-0.5">{m?.role?.role_id || '-'}</div>
                    {m?.role?.role_description && <div className="text-xs text-text-secondary mt-0.5">{m?.role?.role_description}</div>}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                      m?.role?.is_active === 1 ? 'bg-success/10 text-success' : 'bg-muted text-text-secondary'
                    }`}>{m?.role?.is_active === 1 ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {Array.isArray(m?.permissions) && m?.permissions?.length > 0 ? (
                        m?.permissions?.map((p, pi) => (
                          <div key={pi} className="flex flex-wrap items-center gap-1.5">
                            <span className="text-sm text-text-primary">{p?.permission_name}</span>
                            <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-mono">{p?.action}</span>
                            <span className="text-xs text-text-secondary">{p?.route}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-text-secondary">No permissions</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-text-secondary">{m?.role_assignment_id || '-'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderUserRoles = () => (
    <div className="space-y-5">
      {/* Assign Roles to User */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Assign Roles to User</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">Select User</label>
            <div className="relative">
              <input
                type="text"
                value={userSearch}
                onChange={e => { setUserSearch(e?.target?.value); setSelectedUser(''); setShowUserDropdown(true); }}
                onFocus={() => setShowUserDropdown(true)}
                placeholder="Search by name or email..."
                className={inputClass}
              />
              {selectedUser && (
                <div className="mt-1 text-xs text-text-secondary">
                  Selected: {users?.find(u => u?.user_id === selectedUser)?.first_name} {users?.find(u => u?.user_id === selectedUser)?.last_name} ({users?.find(u => u?.user_id === selectedUser)?.email})
                </div>
              )}
              {showUserDropdown && userSearch && (
                <div className="absolute z-10 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {users
                    ?.filter(u => {
                      const q = userSearch?.toLowerCase();
                      return (
                        u?.first_name?.toLowerCase()?.startsWith(q) ||
                        u?.last_name?.toLowerCase()?.startsWith(q) ||
                        u?.email?.toLowerCase()?.includes(q)
                      );
                    })
                    ?.map(u => (
                      <div
                        key={u?.user_id}
                        onClick={() => { setSelectedUser(u?.user_id); setUserSearch(`${u?.first_name} ${u?.last_name}`); setShowUserDropdown(false); }}
                        className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                      >
                        <span className="font-medium text-text-primary">{u?.first_name} {u?.last_name}</span>
                        <span className="text-text-secondary ml-2 text-xs">{u?.email}</span>
                      </div>
                    ))}
                  {users?.filter(u => {
                    const q = userSearch?.toLowerCase();
                    return u?.first_name?.toLowerCase()?.startsWith(q) || u?.last_name?.toLowerCase()?.startsWith(q) || u?.email?.toLowerCase()?.includes(q);
                  })?.length === 0 && (
                    <div className="px-3 py-2 text-sm text-text-secondary">No users found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-2">Select Roles</label>
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
              {roles?.map(r => (
                <label key={r?.role_id} className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer">
                  <input type="checkbox" checked={selectedRoles?.includes(r?.role_id)}
                    onChange={() => toggleRole(r?.role_id)}
                    className="h-4 w-4 rounded border-input text-primary" />
                  <span className="text-sm text-text-primary">{r?.role_name}</span>
                  {r?.role_description && <span className="text-xs text-text-secondary">- {r?.role_description}</span>}
                </label>
              ))}
              {roles?.length === 0 && <div className="text-sm text-text-secondary p-2">No roles available</div>}
            </div>
          </div>
          <button onClick={handleAssignRoles} disabled={!selectedUser || selectedRoles?.length === 0 || submitting}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
            {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Assigning...</span></> : <span>Assign Roles</span>}
          </button>
        </div>
      </div>

      {/* Existing Mappings */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-text-primary">User-Role Mappings</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {['User', 'Assigned Roles']?.map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={2} className="text-center py-8"><Icon name="Loader2" size={20} className="animate-spin text-primary mx-auto" /></td></tr>
              ) : mappings?.length === 0 ? (
                <tr><td colSpan={2} className="text-center py-8 text-text-secondary text-sm">No mappings found</td></tr>
              ) : mappings?.map((m, i) => (
                <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30 align-top">
                  <td className="px-4 py-3">
                    <div className="text-xs font-mono text-text-secondary">{m?.user?.user_id || '-'}</div>
                    <div className="text-sm font-medium text-text-primary mt-0.5">
                      {m?.user?.first_name || ''} {m?.user?.last_name || ''}
                    </div>
                    <div className="text-xs text-text-secondary mt-0.5">{m?.user?.email || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {Array.isArray(m?.roles) && m?.roles?.length > 0 ? (
                        m?.roles?.map((r, ri) => (
                          <div key={ri} className="flex items-center gap-1">
                            <span className="text-sm text-text-primary">{r?.role_name}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              r?.is_active === 1 ? 'bg-success/10 text-success' : 'bg-muted text-text-secondary'
                            }`}>
                              {r?.is_active === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-text-secondary">No roles</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (section === 'role-permissions') {
    return (
      <div className="min-h-screen bg-background">
        <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
          <div className="p-6">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-text-primary">Role Permissions</h1>
              <p className="text-text-secondary mt-1">Map permissions to roles</p>
            </div>
            {error && <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg"><Icon name="AlertCircle" size={16} className="text-error" /><p className="text-sm text-error">{error}</p></div>}
            {success && <div className="mb-4 flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg"><Icon name="CheckCircle" size={16} className="text-success" /><p className="text-sm text-success">{success}</p></div>}
            {renderRolePermissions()}
          </div>
        </main>
      </div>
    );
  }

  if (section === 'user-roles') {
    return (
      <div className="min-h-screen bg-background">
        <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
          <div className="p-6">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-text-primary">User Roles</h1>
              <p className="text-text-secondary mt-1">Map roles to users</p>
            </div>
            {error && <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg"><Icon name="AlertCircle" size={16} className="text-error" /><p className="text-sm text-error">{error}</p></div>}
            {success && <div className="mb-4 flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg"><Icon name="CheckCircle" size={16} className="text-success" /><p className="text-sm text-success">{success}</p></div>}
            {renderUserRoles()}
          </div>
        </main>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">{config?.title}</h1>
              <p className="text-text-secondary mt-1">{config?.description}</p>
            </div>
            <button onClick={() => { setShowForm(!showForm); setForm({}); }}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90">
              <Icon name="Plus" size={16} />
              <span>Create {config?.title?.slice(0, -1)}</span>
            </button>
          </div>

          {/* Create Form */}
          {showForm && (
            <div className="bg-surface border border-border rounded-xl p-5 mb-5">
              <h3 className="text-sm font-semibold text-text-primary mb-4">Create New {config?.title?.slice(0, -1)}</h3>
              <form onSubmit={handleCreate} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {config?.fields?.map(field => (
                    <div key={field?.name}>
                      <label className="text-xs font-medium text-text-secondary block mb-1">
                        {field?.label}{field?.required && <span className="text-error ml-1">*</span>}
                      </label>
                      {field?.type === 'permission_group_select' ? (
                        <select value={form?.[field?.name] || ''} onChange={e => setForm(p => ({ ...p, [field?.name]: e?.target?.value }))}
                          className={inputClass} required={field?.required}>
                          <option value="">Select Permission Group</option>
                          {permissionGroups?.map(g => <option key={g?.group_id} value={g?.group_id}>{g?.group_name}</option>)}
                        </select>
                      ) : field?.type === 'business_select' ? (
                        <select value={form?.[field?.name] || ''} onChange={e => setForm(p => ({ ...p, [field?.name]: e?.target?.value }))}
                          className={inputClass} required={field?.required}>
                          <option value="">Select Business</option>
                          {businesses?.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      ) : field?.type === 'select' ? (
                        <select value={form?.[field?.name] || ''} onChange={e => setForm(p => ({ ...p, [field?.name]: e?.target?.value }))}
                          className={inputClass}>
                          <option value="">Select {field?.label}</option>
                          {field?.options?.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input type={field?.type} value={form?.[field?.name] || ''}
                          onChange={e => setForm(p => ({ ...p, [field?.name]: e?.target?.value }))}
                          placeholder={field?.label} className={inputClass} />
                      )}
                    </div>
                  ))}
                </div>
                {error && <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg"><Icon name="AlertCircle" size={14} className="text-error" /><p className="text-xs text-error">{error}</p></div>}
                <div className="flex space-x-3">
                  <button type="submit" disabled={submitting}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                    {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Creating...</span></> : <span>Create</span>}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {success && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {error && !showForm && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {config?.columns?.map(col => (
                      <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={config?.columns?.length} className="text-center py-12">
                      <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
                    </td></tr>
                  ) : items?.length === 0 ? (
                    <tr><td colSpan={config?.columns?.length} className="text-center py-12">
                      <div className="flex flex-col items-center space-y-2">
                        <Icon name={config?.icon} size={32} className="text-text-secondary" />
                        <p className="text-text-secondary text-sm">No {config?.title?.toLowerCase()} found</p>
                      </div>
                    </td></tr>
                  ) : (
                    items?.map((item, i) => (
                      <tr key={item?.role_id || item?.group_id || item?.permission_id || item?.id || i} className="border-b border-border last:border-0 hover:bg-muted/30 transition-smooth">
                        {config?.dataKeys?.map((key, ki) => (
                          <td key={ki} className="px-4 py-3 text-sm">
                            {key === 'status' ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                item?.[key] === 1 || item?.[key] === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-text-secondary'
                              }`}>
                                {item?.[key] === 1 ? 'Active' : item?.[key] === 0 ? 'Inactive' : item?.[key] || '-'}
                              </span>
                            ) : key === 'action' ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                item?.[key] === 'manage' ? 'bg-purple-100 text-purple-700' :
                                item?.[key] === 'write' ? 'bg-blue-100 text-blue-700' :
                                item?.[key] === 'delete' ? 'bg-red-100 text-red-700' :
                                item?.[key] === 'read' ? 'bg-green-100 text-green-700' : 'bg-primary/10 text-primary'
                              }`}>{item?.[key] || '-'}</span>
                            ) : key === 'group_id' || key === 'permission_id' || key === 'role_id' ? (
                              <span className="font-mono text-xs text-text-secondary">{item?.[key] || '-'}</span>
                            ) : (
                              <span className={ki === 0 ? 'font-medium text-text-primary' : 'text-text-secondary'}>{item?.[key] || '-'}</span>
                            )}
                          </td>
                        ))}
                        {section === 'permission-groups' && (
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleOpenEdit(item)}
                                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
                              >
                                <Icon name="Pencil" size={12} />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDisable(item)}
                                disabled={disablingId === item?.group_id}
                                className={`flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                                  item?.status === 1
                                    ? 'text-error border border-error/30 hover:bg-error/10' :'text-success border border-success/30 hover:bg-success/10'
                                }`}
                              >
                                {disablingId === item?.group_id ? (
                                  <Icon name="Loader2" size={12} className="animate-spin" />
                                ) : (
                                  <Icon name={item?.status === 1 ? 'Ban' : 'CheckCircle'} size={12} />
                                )}
                                <span>{item?.status === 1 ? 'Disable' : 'Enable'}</span>
                              </button>
                            </div>
                          </td>
                        )}
                        {section === 'permissions' && (
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleOpenPermEdit(item)}
                                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
                              >
                                <Icon name="Pencil" size={12} />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handlePermDisable(item)}
                                disabled={permDisablingId === item?.permission_id}
                                className={`flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                                  item?.status === 1
                                    ? 'text-error border border-error/30 hover:bg-error/10' : 'text-success border border-success/30 hover:bg-success/10'
                                }`}
                              >
                                {permDisablingId === item?.permission_id ? (
                                  <Icon name="Loader2" size={12} className="animate-spin" />
                                ) : (
                                  <Icon name={item?.status === 1 ? 'Ban' : 'CheckCircle'} size={12} />
                                )}
                                <span>{item?.status === 1 ? 'Disable' : 'Enable'}</span>
                              </button>
                            </div>
                          </td>
                        )}
                        {section === 'roles' && (
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleOpenRoleEdit(item)}
                                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
                              >
                                <Icon name="Pencil" size={12} />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleRoleDisable(item)}
                                disabled={roleDisablingId === item?.role_id}
                                className={`flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                                  item?.status === 1
                                    ? 'text-error border border-error/30 hover:bg-error/10' : 'text-success border border-success/30 hover:bg-success/10'
                                }`}
                              >
                                {roleDisablingId === item?.role_id ? (
                                  <Icon name="Loader2" size={12} className="animate-spin" />
                                ) : (
                                  <Icon name={item?.status === 1 ? 'Ban' : 'CheckCircle'} size={12} />
                                )}
                                <span>{item?.status === 1 ? 'Disable' : 'Enable'}</span>
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Permission Group Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-sm font-semibold text-text-primary">Edit Permission Group</h3>
              <button onClick={() => { setShowEditModal(false); setEditItem(null); setError(''); }}
                className="text-text-secondary hover:text-text-primary">
                <Icon name="X" size={18} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">
                  Group Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={editForm?.permission_group_name}
                  onChange={e => setEditForm(p => ({ ...p, permission_group_name: e?.target?.value }))}
                  placeholder="Group Name"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">Description</label>
                <input
                  type="text"
                  value={editForm?.permission_group_description}
                  onChange={e => setEditForm(p => ({ ...p, permission_group_description: e?.target?.value }))}
                  placeholder="Description"
                  className={inputClass}
                />
              </div>
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={14} className="text-error" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
              <div className="flex space-x-3 pt-1">
                <button type="submit" disabled={editSubmitting}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                  {editSubmitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Saving...</span></> : <span>Save Changes</span>}
                </button>
                <button type="button" onClick={() => { setShowEditModal(false); setEditItem(null); setError(''); }}
                  className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Permission Modal */}
      {showPermEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-sm font-semibold text-text-primary">Edit Permission</h3>
              <button onClick={() => { setShowPermEditModal(false); setPermEditItem(null); setError(''); }}
                className="text-text-secondary hover:text-text-primary">
                <Icon name="X" size={18} />
              </button>
            </div>
            <form onSubmit={handlePermUpdate} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">
                  Permission Group <span className="text-error">*</span>
                </label>
                <select
                  value={permEditForm?.permission_group_id}
                  onChange={e => setPermEditForm(p => ({ ...p, permission_group_id: e?.target?.value }))}
                  className={inputClass}
                  required
                >
                  <option value="">Select Permission Group</option>
                  {permissionGroups?.map(g => <option key={g?.group_id} value={g?.group_id}>{g?.group_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">
                  Permission Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={permEditForm?.permission_name}
                  onChange={e => setPermEditForm(p => ({ ...p, permission_name: e?.target?.value }))}
                  placeholder="Permission Name"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">Description</label>
                <input
                  type="text"
                  value={permEditForm?.permission_description}
                  onChange={e => setPermEditForm(p => ({ ...p, permission_description: e?.target?.value }))}
                  placeholder="Description"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">
                  Action <span className="text-error">*</span>
                </label>
                <select
                  value={permEditForm?.action}
                  onChange={e => setPermEditForm(p => ({ ...p, action: e?.target?.value }))}
                  className={inputClass}
                  required
                >
                  {['read', 'write', 'delete', 'manage']?.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">
                  Route <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={permEditForm?.route}
                  onChange={e => setPermEditForm(p => ({ ...p, route: e?.target?.value }))}
                  placeholder="/route/path"
                  className={inputClass}
                  required
                />
              </div>
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={14} className="text-error" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
              <div className="flex space-x-3 pt-1">
                <button type="submit" disabled={permEditSubmitting}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                  {permEditSubmitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Saving...</span></> : <span>Save Changes</span>}
                </button>
                <button type="button" onClick={() => { setShowPermEditModal(false); setPermEditItem(null); setError(''); }}
                  className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showRoleEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-sm font-semibold text-text-primary">Edit Role</h3>
              <button onClick={() => { setShowRoleEditModal(false); setRoleEditItem(null); setError(''); }}
                className="text-text-secondary hover:text-text-primary">
                <Icon name="X" size={18} />
              </button>
            </div>
            <form onSubmit={handleRoleUpdate} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">
                  Role Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={roleEditForm?.role_name}
                  onChange={e => setRoleEditForm(p => ({ ...p, role_name: e?.target?.value }))}
                  placeholder="Role Name"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">Description</label>
                <input
                  type="text"
                  value={roleEditForm?.role_description}
                  onChange={e => setRoleEditForm(p => ({ ...p, role_description: e?.target?.value }))}
                  placeholder="Description"
                  className={inputClass}
                />
              </div>
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={14} className="text-error" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
              <div className="flex space-x-3 pt-1">
                <button type="submit" disabled={roleEditSubmitting}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                  {roleEditSubmitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Saving...</span></> : <span>Save Changes</span>}
                </button>
                <button type="button" onClick={() => { setShowRoleEditModal(false); setRoleEditItem(null); setError(''); }}
                  className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RbacPage;
